import axios from 'axios';
import { GET_ERRORS, CLEAR_ERRORS } from './types';
import { tokenConfig } from './authActions';

export const GET_CONVERSATIONS = 'GET_CONVERSATIONS';
export const GET_MESSAGES = 'GET_MESSAGES';
export const SEND_MESSAGE = 'SEND_MESSAGE';
export const SET_ACTIVE_CONVERSATION = 'SET_ACTIVE_CONVERSATION';
export const MARK_AS_READ = 'MARK_AS_READ';
export const GET_UNREAD_COUNT = 'GET_UNREAD_COUNT';
export const DELETE_MESSAGE = 'DELETE_MESSAGE';
export const MESSAGES_LOADING = 'MESSAGES_LOADING';
export const CLEAR_MESSAGES = 'CLEAR_MESSAGES';
export const GET_AVAILABLE_USERS = 'GET_AVAILABLE_USERS';
export const ADD_CONVERSATION_PARTICIPANT = 'ADD_CONVERSATION_PARTICIPANT';
export const REMOVE_CONVERSATION_PARTICIPANT = 'REMOVE_CONVERSATION_PARTICIPANT';
export const UPDATE_ACTIVE_CONVERSATION = 'UPDATE_ACTIVE_CONVERSATION';
export const DELETE_CONVERSATION = 'DELETE_CONVERSATION';
export const EDIT_MESSAGE = 'EDIT_MESSAGE';
export const REACT_TO_MESSAGE = 'REACT_TO_MESSAGE';
export const SEARCH_MESSAGES = 'SEARCH_MESSAGES';
export const CLEAR_SEARCH = 'CLEAR_SEARCH';
export const PIN_MESSAGE = 'PIN_MESSAGE';
export const TOGGLE_MUTE = 'TOGGLE_MUTE';

// Debounce helper for preventing rapid-fire actions
const debounceMap = new Map();
const debounce = (func, delay, key) => {
  return (...args) => {
    if (debounceMap.has(key)) {
      clearTimeout(debounceMap.get(key));
    }
    debounceMap.set(key, setTimeout(() => {
      func(...args);
      debounceMap.delete(key);
    }, delay));
  };
};

// Get conversations list
export const getConversations = (skipLoading = false) => async (dispatch, getState) => {
  const hasConversations = getState().message.conversations.length > 0;
  // Only show loading spinner on the very first fetch (empty list)
  if (!skipLoading && !hasConversations) {
    dispatch(setMessagesLoading());
  }
  try {
    const res = await axios.get('/api/messages/conversations', tokenConfig(getState));
    dispatch({
      type: GET_CONVERSATIONS,
      payload: res.data
    });
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: err.response?.data || { msg: 'Failed to fetch conversations' }
    });
  }
};

// Get messages for a conversation
export const getMessages = (type, id, page = 1, skipLoading = false) => async (dispatch, getState) => {
  const cacheKey = `${type}/${id}`;
  const hasCached = (getState().message.messagesCache[cacheKey] || []).length > 0;
  // Only show loading spinner if no cached messages exist for this conversation
  if (!skipLoading && !hasCached && page === 1) {
    dispatch(setMessagesLoading());
  }
  try {
    const res = await axios.get(`/api/messages/conversation/${type}/${id}?page=${page}&limit=50`, tokenConfig(getState));
    dispatch({
      type: GET_MESSAGES,
      payload: {
        ...res.data,
        append: page > 1,
        cacheKey: page === 1 ? cacheKey : null  // only cache page-1 results
      }
    });
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: err.response?.data || { msg: 'Failed to fetch messages' }
    });
  }
};

// Send a message
export const sendMessage = (messageData, attachments = null) => async (dispatch, getState) => {
  try {
    // Process attachments if any
    const processedAttachments = [];
    if (attachments && attachments.length > 0) {
      for (const file of attachments) {
        const base64Data = await convertFileToBase64(file);
        processedAttachments.push({
          fileName: file.name,
          fileData: base64Data,
          fileType: file.type,
          fileSize: file.size
        });
      }
    }

    const payload = {
      ...messageData,
      attachments: processedAttachments.length > 0 ? processedAttachments : undefined
    };

    const res = await axios.post('/api/messages/send', payload, tokenConfig(getState));
    
    dispatch({
      type: SEND_MESSAGE,
      payload: res.data
    });

    dispatch(clearErrors());

    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: err.response?.data || { msg: 'Failed to send message' }
    });
    throw err;
  }
};

// Helper function to convert file to base64
const convertFileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
};

// Mark messages as read
export const markAsRead = (messageId) => async (dispatch, getState) => {
  try {
    await axios.put(`/api/messages/${messageId}/read`, {}, tokenConfig(getState));
    dispatch({
      type: MARK_AS_READ,
      payload: messageId
    });
  } catch (err) {
    console.error('Error marking message as read:', err);
  }
};

// Delete a message
export const deleteMessage = (messageId) => async (dispatch, getState) => {
  try {
    await axios.delete(`/api/messages/${messageId}`, tokenConfig(getState));
    dispatch({
      type: DELETE_MESSAGE,
      payload: messageId
    });
    
    dispatch(clearErrors());
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: err.response?.data || { msg: 'Failed to delete message' }
    });
  }
};

// Edit a message
export const editMessage = (messageId, content) => async (dispatch, getState) => {
  try {
    const res = await axios.put(`/api/messages/${messageId}/edit`, { content }, tokenConfig(getState));
    dispatch({
      type: EDIT_MESSAGE,
      payload: res.data
    });
    dispatch(clearErrors());
    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: err.response?.data || { msg: 'Failed to edit message' }
    });
    throw err;
  }
};

// React to a message
export const reactToMessage = (messageId, emoji) => async (dispatch, getState) => {
  try {
    const res = await axios.post(`/api/messages/${messageId}/react`, { emoji }, tokenConfig(getState));
    dispatch({
      type: REACT_TO_MESSAGE,
      payload: res.data
    });
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: err.response?.data || { msg: 'Failed to react to message' }
    });
  }
};

// Search messages
export const searchMessages = (query, conversationId, type) => async (dispatch, getState) => {
  try {
    const params = new URLSearchParams({ q: query });
    if (conversationId) params.append('conversationId', conversationId);
    if (type) params.append('type', type);
    const res = await axios.get(`/api/messages/search?${params}`, tokenConfig(getState));
    dispatch({
      type: SEARCH_MESSAGES,
      payload: res.data
    });
    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: err.response?.data || { msg: 'Search failed' }
    });
    return { messages: [] };
  }
};

export const clearSearch = () => ({ type: CLEAR_SEARCH });

// Pin/unpin a message
export const pinMessage = (messageId) => async (dispatch, getState) => {
  try {
    const res = await axios.put(`/api/messages/${messageId}/pin`, {}, tokenConfig(getState));
    dispatch({
      type: PIN_MESSAGE,
      payload: res.data
    });
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: err.response?.data || { msg: 'Failed to pin message' }
    });
  }
};

// Toggle mute on a conversation
export const toggleMute = (type, id) => async (dispatch, getState) => {
  try {
    const res = await axios.put(`/api/messages/conversation/${type}/${id}/mute`, {}, tokenConfig(getState));
    dispatch({
      type: TOGGLE_MUTE,
      payload: res.data
    });
    // Refresh conversations to update muted status
    dispatch(getConversations(true));
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: err.response?.data || { msg: 'Failed to toggle mute' }
    });
  }
};

// Get unread count
export const getUnreadCount = () => async (dispatch, getState) => {
  try {
    const res = await axios.get('/api/messages/unread-count', tokenConfig(getState));
    dispatch({
      type: GET_UNREAD_COUNT,
      payload: res.data.count
    });
  } catch (err) {
    console.error('Error getting unread count:', err);
  }
};

// Debounced version for real-time updates (prevents spam)
export const getUnreadCountDebounced = debounce((dispatch) => {
  dispatch(getUnreadCount());
}, 1000, 'unreadCount');

// Debounced conversations refresh for real-time updates
export const getConversationsDebounced = debounce((dispatch, skipLoading = true) => {
  dispatch(getConversations(skipLoading));
}, 500, 'conversations');

// Set active conversation (clears messages for new conversation)
export const setActiveConversation = (conversation) => ({
  type: SET_ACTIVE_CONVERSATION,
  payload: conversation
});

// Update active conversation (preserves messages for same conversation)
export const updateActiveConversation = (conversation) => ({
  type: UPDATE_ACTIVE_CONVERSATION,
  payload: conversation
});

// Get available users for creating custom conversations
export const getAvailableUsers = () => async (dispatch, getState) => {
  try {
    const res = await axios.get('/api/messages/available-users', tokenConfig(getState));
    dispatch({
      type: GET_AVAILABLE_USERS,
      payload: res.data
    });
    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: err.response?.data || { msg: 'Failed to fetch available users' }
    });
    return [];
  }
};

// Create custom conversation
export const createCustomConversation = (conversationData) => async (dispatch, getState) => {
  try {
    const res = await axios.post('/api/messages/create-conversation', conversationData, tokenConfig(getState));
    
    // After creating the conversation, refresh the conversations list
    dispatch(getConversations());
    
    dispatch(clearErrors());
    
    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: err.response?.data || { msg: 'Failed to create custom conversation' }
    });
    throw err;
  }
};

// Clear messages
export const clearMessages = () => ({
  type: CLEAR_MESSAGES
});

// Set loading state
export const setMessagesLoading = () => ({
  type: MESSAGES_LOADING
});

// Add participant to conversation
export const addConversationParticipant = (conversationId, userId) => async (dispatch, getState) => {
  try {
    const res = await axios.put(`/api/messages/conversation/${conversationId}/add-participant`, 
      { userId }, 
      tokenConfig(getState)
    );
    
    dispatch({
      type: ADD_CONVERSATION_PARTICIPANT,
      payload: {
        conversationId,
        participant: res.data.conversation.participants.find(p => p._id === userId)
      }
    });
    
    // Refresh conversations to update participant count
    dispatch(getConversations(true));
    
    dispatch(clearErrors());
    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: err.response?.data || { msg: 'Failed to add participant' }
    });
    throw err;
  }
};

// Remove participant from conversation
export const removeConversationParticipant = (conversationId, userId) => async (dispatch, getState) => {
  try {
    const res = await axios.put(`/api/messages/conversation/${conversationId}/remove-participant`, 
      { userId }, 
      tokenConfig(getState)
    );
    
    dispatch({
      type: REMOVE_CONVERSATION_PARTICIPANT,
      payload: {
        conversationId,
        userId
      }
    });
    
    // Refresh conversations to update participant count
    dispatch(getConversations(true));
    
    dispatch(clearErrors());
    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: err.response?.data || { msg: 'Failed to remove participant' }
    });
    throw err;
  }
};

// Delete conversation and all its messages
export const deleteConversation = (conversationId) => async (dispatch, getState) => {
  try {
    const res = await axios.delete(`/api/messages/conversation/${conversationId}`, tokenConfig(getState));
    
    dispatch({
      type: DELETE_CONVERSATION,
      payload: conversationId
    });
    
    // Refresh conversations list
    dispatch(getConversations(true));
    
    dispatch(clearErrors());
    return res.data;
  } catch (err) {
    dispatch({
      type: GET_ERRORS,
      payload: err.response?.data || { msg: 'Failed to delete conversation' }
    });
    throw err;
  }
};

// Clear errors
export const clearErrors = () => ({
  type: CLEAR_ERRORS
});
